import { icons, type LucideProps } from 'lucide-react';

interface DynamicIconProps extends LucideProps {
  name: string;
}

/**
 * Dev-mode-only diagnostic for an unresolvable icon name. Gated on
 * `import.meta.env.DEV` (Vite's standard flag — dead-code-eliminated in
 * every built bundle, including the local-stack Docker build) so this never
 * fires in a deployed environment: DynamicIcon's fallback must stay silent
 * for end users (a report's dynamically-sourced presentation.icon has no
 * build-time validation), but a developer running `npm run dev` should see
 * why a glyph didn't render.
 */
function warnUnresolvedIcon(description: string): void {
  if (import.meta.env.DEV) {
    console.warn(`[DynamicIcon] ${description} — falling back to "file-text".`);
  }
}

/**
 * Renders a lucide-react icon by name string.
 * Falls back to file-text if the name is missing or not found — this is the
 * one place a dynamically-sourced report's presentation.icon (no build-time
 * validation, unlike manifest.json icons) can go through unchecked, so the
 * fallback must never crash the shell.
 */
export function DynamicIcon({ name, ...props }: DynamicIconProps) {
  if (!name) {
    warnUnresolvedIcon('No icon name provided');
    const Fallback = icons.FileText;
    return Fallback ? <Fallback {...props} /> : null;
  }

  // Convert kebab-case to PascalCase: "plus-circle" → "PlusCircle"
  const pascalName = name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

  const IconComponent = icons[pascalName as keyof typeof icons];

  if (!IconComponent) {
    warnUnresolvedIcon(`Unresolvable icon name "${name}" (resolved as "${pascalName}")`);
    const Fallback = icons.FileText;
    return Fallback ? <Fallback {...props} /> : null;
  }

  return <IconComponent {...props} />;
}
